import { useState } from "react"

import { Checkbox, Label } from "@atlant/ui"

import { graphql } from "#gql"
import type { InvoiceUploadListQuery } from "#gql/graphql.ts"

import { UploadQueueTable } from "./components/upload-queue-table.tsx"

export const InvoiceUploadListDocument = graphql(/* GraphQL */ `
  query InvoiceUploadList($input: QueryInvoiceUploadListInput!) {
    invoiceUploadList(input: $input) {
      id
      fileName
      mimeType
      blobName
      size
      status
      documentKind
      purNumber
      invoiceId
      postError
      vendor
      serviceMonth
      serviceMonthDisplay
      late
      classificationNote
      createdDate {
        milliseconds
        displaydate
        displaytime
      }
    }
  }
`)

export type InvoiceUploadRow = InvoiceUploadListQuery["invoiceUploadList"][number]

interface UploadQueueProps {
  uploads: InvoiceUploadRow[]
  isLoading: boolean
  search: string
}

export const UploadQueue = ({ uploads, isLoading, search }: UploadQueueProps) => {
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false)

  // Uploaded names can be decomposed (NFD); compare composed on both sides
  const term = search.normalize("NFC").trim().toLowerCase()
  const rows = uploads.filter(
    ({ status, fileName }) =>
      (!needsReviewOnly || status === "AWAITING_REVIEW") &&
      (!term || fileName.normalize("NFC").toLowerCase().includes(term)),
  )

  return (
    // Hugs its rows, up to the space below the tabs; past that the table scrolls
    <div className="bg-card flex max-h-full flex-col gap-4 rounded-lg border p-4">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold">Upload queue</h2>
          <p className="text-muted-foreground text-sm">
            Invoices extracted from monthly reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="needs-review-only"
            checked={needsReviewOnly}
            onCheckedChange={(checked) => setNeedsReviewOnly(checked === true)}
          />
          <Label htmlFor="needs-review-only">Needs review only</Label>
        </div>
      </div>
      <UploadQueueTable
        rows={rows}
        isLoading={isLoading}
        emptyText={
          term
            ? "No uploads match your search"
            : needsReviewOnly
              ? "No uploads awaiting review"
              : "No uploads yet"
        }
      />
    </div>
  )
}
